import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bot, Sparkles, Loader2, RefreshCcw, Play } from "lucide-react";

/**
 * Qualification profile + test reply — feeds Claude at every triage call.
 *
 * The buyer picks a sector (pre-fills qualifiers/disqualifiers/tone), tunes the
 * lists to their products, sets the confidence threshold, and ticks which
 * intents are allowed to auto-action. A "Test reply" tool runs a synthetic
 * reply through the LLM and shows the parsed JSON, so the buyer can verify the
 * profile is dialled in before any spend.
 */
export default function ReplyTriageProfile() {
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [testing, setTesting]   = useState(false);
  const [sectors, setSectors]   = useState([]);
  const [defaultThr, setDefThr] = useState(0.85);
  const [form, setForm]         = useState(null);
  const [testText, setTestText] = useState("");
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/client/triage/profile");
      setSectors(r.data.available_sectors || []);
      setDefThr(r.data.default_threshold ?? 0.85);
      setForm({
        sector:               r.data.profile.sector,
        context:              r.data.profile.context || "",
        tone:                 r.data.profile.tone || "",
        qualifiers:           (r.data.profile.qualifiers || []).join("\n"),
        disqualifiers:        (r.data.profile.disqualifiers || []).join("\n"),
        confidence_threshold: r.data.profile.confidence_threshold ?? 0.85,
        auto_flag_dq:         !!r.data.profile.auto_flag_dq,
        auto_schedule:        !!r.data.profile.auto_schedule,
        auto_send_response:   !!r.data.profile.auto_send_response,
        auto_trigger_return:  !!r.data.profile.auto_trigger_return,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const refreshFromTemplate = async () => {
    if (!form?.sector) return;
    try {
      const r = await api.get(`/client/triage/sector-template/${form.sector}`);
      setForm({
        ...form,
        context:       r.data.context,
        tone:          r.data.tone,
        qualifiers:    (r.data.qualifiers || []).join("\n"),
        disqualifiers: (r.data.disqualifiers || []).join("\n"),
      });
      toast.success(`Loaded ${form.sector} template`);
    } catch (e) {
      toast.error("Could not reload template");
    }
  };

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload = {
        sector:               form.sector,
        context:              form.context,
        tone:                 form.tone,
        qualifiers:           form.qualifiers.split("\n").map((s) => s.trim()).filter(Boolean),
        disqualifiers:        form.disqualifiers.split("\n").map((s) => s.trim()).filter(Boolean),
        confidence_threshold: Number(form.confidence_threshold),
        auto_flag_dq:         form.auto_flag_dq,
        auto_schedule:        form.auto_schedule,
        auto_send_response:   form.auto_send_response,
        auto_trigger_return:  form.auto_trigger_return,
      };
      await api.put("/client/triage/profile", payload);
      toast.success("Profile saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    if (!testText.trim()) {
      toast.error("Enter a reply text to test");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/client/triage/test-reply", { text: testText });
      if (r.data?.ok) {
        setTestResult(r.data.parsed);
      } else {
        toast.error(r.data?.error || "LLM did not respond");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading || !form) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="space-y-5" id="triage">
      <Card className="rounded-xl border-slate-200 shadow-soft">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
            <div className="flex items-start gap-3 min-w-0">
              <span className="inline-grid place-items-center size-10 rounded-md bg-[#0F172A] text-white shrink-0">
                <Bot className="size-4" />
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-[15px] font-semibold text-slate-900">AI Reply Triage</h3>
                  <Badge variant="outline" className="rounded-md text-[10.5px] border-slate-200 font-medium">£0.01 suggested · £0.03 auto-action</Badge>
                </div>
                <p className="text-[12.5px] text-slate-500 mt-1 max-w-2xl leading-relaxed">
                  Claude reads every reply, classifies the intent (hot / reschedule / DQ / question / unsubscribe), extracts structured data, and either suggests an action or executes it (if you let it). Tune the profile below to your products.
                </p>
              </div>
            </div>
          </div>

          {/* Sector + reset */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.1em] font-semibold text-slate-600">Sector</Label>
              <Select value={form.sector} onValueChange={(v) => setForm({ ...form, sector: v })}>
                <SelectTrigger className="rounded-md h-9 text-[13px] border-slate-200" data-testid="triage-sector"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {sectors.map((s) => <SelectItem key={s} value={s}>{s.replace("-", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-[11px] uppercase tracking-[0.1em] font-semibold text-slate-600">Tone for suggested responses</Label>
              <Input
                value={form.tone}
                onChange={(e) => setForm({ ...form, tone: e.target.value })}
                placeholder="Professional, FCA-conscious, no pressure"
                className="rounded-md h-9 text-[13px] border-slate-200 focus-visible:ring-1 focus-visible:ring-[#0F172A]"
                data-testid="triage-tone"
              />
            </div>
          </div>

          <div className="space-y-2 mb-5">
            <Label className="text-[11px] uppercase tracking-[0.1em] font-semibold text-slate-600">Sector context (1-2 sentences)</Label>
            <Textarea
              rows={2}
              value={form.context}
              onChange={(e) => setForm({ ...form, context: e.target.value })}
              className="text-[12.5px] border-slate-200 focus-visible:ring-1 focus-visible:ring-[#0F172A] resize-none"
              data-testid="triage-context"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
            <div className="rounded-md border border-slate-200 p-4 space-y-2 bg-white">
              <div className="flex items-center justify-between">
                <Label className="text-[11.5px] uppercase tracking-[0.1em] font-semibold text-slate-700">Qualifiers</Label>
                <span className="text-[10.5px] text-slate-500">One per line</span>
              </div>
              <Textarea
                rows={6}
                value={form.qualifiers}
                onChange={(e) => setForm({ ...form, qualifiers: e.target.value })}
                className="font-mono text-[12px] border-slate-200 focus-visible:ring-1 focus-visible:ring-[#0F172A] resize-none"
                data-testid="triage-qualifiers"
              />
            </div>
            <div className="rounded-md border border-slate-200 p-4 space-y-2 bg-white">
              <div className="flex items-center justify-between">
                <Label className="text-[11.5px] uppercase tracking-[0.1em] font-semibold text-slate-700">Disqualifiers</Label>
                <span className="text-[10.5px] text-slate-500">One per line</span>
              </div>
              <Textarea
                rows={6}
                value={form.disqualifiers}
                onChange={(e) => setForm({ ...form, disqualifiers: e.target.value })}
                className="font-mono text-[12px] border-slate-200 focus-visible:ring-1 focus-visible:ring-[#0F172A] resize-none"
                data-testid="triage-disqualifiers"
              />
            </div>
          </div>

          {/* Threshold + auto-action toggles */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
            <div className="rounded-md border border-slate-200 p-4 bg-white">
              <Label className="text-[11px] uppercase tracking-[0.1em] font-semibold text-slate-700">Confidence threshold</Label>
              <p className="text-[11px] text-slate-500 mt-0.5 mb-3">Below this, replies go to the review queue. Default: {Math.round(defaultThr * 100)}%</p>
              <div className="flex items-center gap-3">
                <Input
                  type="range"
                  min={0.5}
                  max={0.99}
                  step={0.01}
                  value={form.confidence_threshold}
                  onChange={(e) => setForm({ ...form, confidence_threshold: parseFloat(e.target.value) })}
                  className="flex-1"
                  data-testid="triage-threshold"
                />
                <span className="text-[14px] font-semibold tabular-nums text-slate-900 w-12 text-right">{Math.round(form.confidence_threshold * 100)}%</span>
              </div>
            </div>
            <div className="rounded-md border border-slate-200 p-4 bg-white space-y-3">
              <Label className="text-[11px] uppercase tracking-[0.1em] font-semibold text-slate-700">Auto-actions allowed</Label>
              <p className="text-[11px] text-slate-500 mb-1">Each intent type can be toggled separately. Suggested-mode for off, auto-action for on.</p>
              <ToggleRow label="Auto-flag Disqualifiers" desc="6h grace before refund fires" checked={form.auto_flag_dq} onChange={(v) => setForm({ ...form, auto_flag_dq: v })} testid="triage-auto-dq" />
              <ToggleRow label="Auto-schedule Callbacks" desc="Stamp on lead + send confirmation" checked={form.auto_schedule} onChange={(v) => setForm({ ...form, auto_schedule: v })} testid="triage-auto-schedule" />
              <ToggleRow label="Auto-send Responses" desc="Send the suggested reply unattended" checked={form.auto_send_response} onChange={(v) => setForm({ ...form, auto_send_response: v })} testid="triage-auto-send" />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-100">
            <Button variant="outline" onClick={refreshFromTemplate} className="rounded-md h-9 px-4 text-[12.5px]" data-testid="triage-reset-template">
              <RefreshCcw className="size-3.5" /> Reload sector template
            </Button>
            <Button onClick={save} disabled={saving} className="rounded-md bg-[#0F172A] hover:bg-[#1e293b] text-white h-9 px-5 text-[13px] font-medium" data-testid="triage-save">
              {saving ? <Loader2 className="size-3.5 animate-spin" /> : "Save profile"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Test reply */}
      <Card className="rounded-xl border-slate-200 shadow-soft">
        <CardContent className="p-6 space-y-4">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="size-4 text-[#0055FF]" />
              <h3 className="text-[14.5px] font-semibold text-slate-900">Test your profile</h3>
            </div>
            <p className="text-[12px] text-slate-500 mt-1">Paste a sample reply and see how Claude would classify it with your current profile. Free — no wallet charge, no action triggered.</p>
          </div>

          <Textarea
            rows={3}
            placeholder="e.g. Yes — please call me tomorrow at 3pm. I have a £40k deposit ready."
            value={testText}
            onChange={(e) => setTestText(e.target.value)}
            className="text-[13px] border-slate-200 focus-visible:ring-1 focus-visible:ring-[#0F172A] resize-none"
            data-testid="triage-test-text"
          />

          <Button onClick={runTest} disabled={testing} variant="outline" className="rounded-md h-9 px-4 text-[12.5px]" data-testid="triage-test-run">
            {testing ? <Loader2 className="size-3.5 animate-spin" /> : <Play className="size-3.5" />} Run test
          </Button>

          {testResult && (
            <div className="rounded-md border border-slate-200 bg-slate-50/50 p-4 text-[12.5px]" data-testid="triage-test-result">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11.5px]">
                <Stat label="Intent" value={testResult.intent} />
                <Stat label="Confidence" value={`${Math.round((testResult.confidence || 0) * 100)}%`} />
                <Stat label="Hot score" value={`${Math.round((testResult.hot_score || 0) * 100)}%`} />
                <Stat label="Action" value={testResult.callback_iso ? "callback" : testResult.dq_reason ? "DQ" : "—"} />
              </div>
              {testResult.dq_reason && <div className="mt-3 text-[12px] text-rose-700"><span className="font-semibold">DQ reason:</span> {testResult.dq_reason}</div>}
              {testResult.callback_iso && <div className="mt-3 text-[12px] text-[#0F172A]"><span className="font-semibold">Callback:</span> {new Date(testResult.callback_iso).toLocaleString()}</div>}
              {testResult.suggested_response && <div className="mt-3 text-[12px] text-slate-700 leading-relaxed"><span className="font-semibold text-slate-900">Suggested reply:</span><br />{testResult.suggested_response}</div>}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ToggleRow({ label, desc, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[12.5px] text-slate-800 font-medium">{label}</div>
        <div className="text-[10.5px] text-slate-500">{desc}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-[0.12em] text-slate-500 font-semibold">{label}</div>
      <div className="text-[14px] text-slate-900 font-semibold mt-0.5 capitalize">{value}</div>
    </div>
  );
}
